import React, { useState } from 'react';
import { useEquipment } from '../../../context/EquipmentContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { uploadFile } from '../../../utils/storageUtils';
import { compressImage } from '../../../utils/imageUtils';

const ReturnEquipmentModal = ({ isOpen, onClose, onSuccess, borrow }) => {
  const { returnEquipment } = useEquipment();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [condition, setCondition] = useState('GOOD');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !borrow) return null;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check plan-based limit
    const userPlan = currentUser?.plan || 'FREE';
    const maxImages = userPlan === 'PRO' ? 3 : 1;
    const remainingSlots = maxImages - images.length;

    if (remainingSlots <= 0) {
      alert(`ถึงขีดจำกัดการอัพโหลดรูปภาพแล้ว (${maxImages} รูป${userPlan === 'FREE' ? ' - สมัคร Pro Plan เพื่ออัพโหลดได้สูงสุด 3 รูป' : ''})`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      alert(`สามารถอัพโหลดได้อีก ${remainingSlots} รูปเท่านั้น (สูงสุด ${maxImages} รูป)`);
    }

    try {
      setUploadingImages(true);
      const uploadedUrls = [];

      for (const file of filesToUpload) {
        // Compress image
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8
        });

        // Upload to Firebase Storage
        const fileName = `equipment-return-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const path = `equipment/${currentTeam.id}/${fileName}`;
        const url = await uploadFile(compressedFile, path);
        uploadedUrls.push(url);
      }

      setImages([...images, ...uploadedUrls]);
    } catch (err) {
      console.error('Error uploading images:', err);
      alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!condition) {
      alert('กรุณาเลือกสภาพอุปกรณ์');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await returnEquipment(borrow.equipmentId, {
        condition,
        notes: notes.trim(),
        images
      }, borrow.id); // Pass borrowId to avoid querying again

      if (result.success) {
        alert('ส่งคำขอคืนอุปกรณ์สำเร็จ รอการอนุมัติจากหัวหน้าทีม');
        onSuccess();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error returning equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">📥 คืนอุปกรณ์</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Equipment Info */}
          {borrow.equipment && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🩺</span>
                <div>
                  <h3 className="font-semibold text-gray-800">{borrow.equipment.name}</h3>
                  <p className="text-sm text-gray-600">{borrow.equipment.code}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">วันที่ยืม:</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(borrow.borrowDate || borrow.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">วันที่คืนที่คาดหวัง:</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(borrow.expectedReturnDate)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Condition Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              ✅ สภาพอุปกรณ์ <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="condition"
                  value="GOOD"
                  checked={condition === 'GOOD'}
                  onChange={(e) => setCondition(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium text-gray-800">ดี (Good)</span>
                  <p className="text-xs text-gray-500">อุปกรณ์อยู่ในสภาพดีพร้อมใช้งาน</p>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="condition"
                  value="DAMAGED"
                  checked={condition === 'DAMAGED'}
                  onChange={(e) => setCondition(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium text-gray-800">เสียหาย (Damaged)</span>
                  <p className="text-xs text-gray-500">อุปกรณ์มีรอยเสียหายแต่ยังใช้งานได้</p>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="condition"
                  value="NEEDS_REPAIR"
                  checked={condition === 'NEEDS_REPAIR'}
                  onChange={(e) => setCondition(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium text-gray-800">ต้องซ่อม (Needs Repair)</span>
                  <p className="text-xs text-gray-500">อุปกรณ์ต้องส่งซ่อมก่อนใช้งาน</p>
                </div>
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📝 รายละเอียดปัญหา (ถ้ามี)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="ระบุรายละเอียดปัญหา..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📷 Upload รูปภาพ (ถ้ามี)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploadingImages}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {uploadingImages && (
              <p className="text-sm text-blue-600 mt-2">กำลังอัปโหลด...</p>
            )}
            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {images.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Equipment ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !condition}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'กำลังคืน...' : 'ยืนยันการคืน'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnEquipmentModal;

